/**
 * Numbers, money and times for Nepal: lakh grouping (12,34,567), "Rs.", and
 * times shown in Nepal time (UTC+5:45) whatever the browser's zone.
 */

export const NEPAL_TZ = 'Asia/Kathmandu'
const DEV = '०१२३४५६७८९'

let nepaliDigits = false
/** Called by the language switch: Nepali shows Devanagari digits. */
export function setNepaliDigits(on: boolean) {
  nepaliDigits = on
}
export const digits = (s: string) => (nepaliDigits ? s.replace(/[0-9]/g, (d) => DEV[Number(d)]) : s)

const money = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const money0 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 })
const num1 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 1 })
const num2 = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const int = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 })

const rs = () => (nepaliDigits ? 'रू.' : 'Rs.')

export const npr = (n: number | null | undefined) => (n == null || Number.isNaN(n) ? '–' : digits(`${rs()} ${money.format(n)}`))
/** Rs. 1,000 for whole-rupee amounts (top-up amounts and limits). */
export const nprWhole = (n: number | null | undefined) => (n == null || Number.isNaN(n) ? '–' : digits(`${rs()} ${money0.format(n)}`))
/** Rs. 12.3L / 4.5K for tight spaces (KPI tiles, chart axes). */
export function nprCompact(n: number | null | undefined) {
  if (n == null) return '–'
  const a = Math.abs(n)
  if (a >= 1e7) return digits(`${rs()} ${num1.format(n / 1e7)} Cr`)
  if (a >= 1e5) return digits(`${rs()} ${num1.format(n / 1e5)} L`)
  if (a >= 1e4) return digits(`${rs()} ${num1.format(n / 1e3)} K`)
  return digits(`${rs()} ${money0.format(n)}`)
}
export const kwh = (n: number | null | undefined) => (n == null ? '–' : digits(`${num2.format(n)} kWh`))
export const kw = (n: number | null | undefined) => (n == null ? '–' : digits(`${num1.format(n)} kW`))
export const count = (n: number | null | undefined) => (n == null ? '–' : digits(int.format(n)))
export const percent = (n: number | null | undefined, max = 0) =>
  n == null ? '–' : digits(`${new Intl.NumberFormat('en-IN', { maximumFractionDigits: max }).format(n)}%`)

/** Preserve explicit API offsets (including financial +05:45); legacy unzoned times are UTC. */
export function parseServerTime(s: string | null | undefined): Date | null {
  if (!s) return null
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`)
  return Number.isNaN(d.getTime()) ? null : d
}

const dtf = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-GB', { timeZone: NEPAL_TZ, ...opts })
const fmtDate = dtf({ day: 'numeric', month: 'short', year: 'numeric' })
const fmtDateTime = dtf({ day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
const fmtTime = dtf({ hour: 'numeric', minute: '2-digit', hour12: true })
const fmtShort = dtf({ day: 'numeric', month: 'short' })

export const date = (s: string | Date | null | undefined) => {
  const d = s instanceof Date ? s : parseServerTime(s)
  return d ? digits(fmtDate.format(d)) : '–'
}
export const dateTime = (s: string | Date | null | undefined) => {
  const d = s instanceof Date ? s : parseServerTime(s)
  return d ? digits(fmtDateTime.format(d)) : '–'
}
export const time = (s: string | Date | null | undefined) => {
  const d = s instanceof Date ? s : parseServerTime(s)
  return d ? digits(fmtTime.format(d)) : '–'
}
export const shortDate = (d: Date) => digits(fmtShort.format(d))

/** "3 min ago", "2 h ago", "4 d ago". */
export function ago(s: string | Date | null | undefined, now = Date.now()) {
  const d = s instanceof Date ? s : parseServerTime(s)
  if (!d) return '–'
  const sec = Math.max(0, Math.round((now - d.getTime()) / 1000))
  if (sec < 60) return digits(`${sec}s ago`)
  if (sec < 3600) return digits(`${Math.round(sec / 60)} min ago`)
  if (sec < 86400) return digits(`${Math.round(sec / 3600)} h ago`)
  return digits(`${Math.round(sec / 86400)} d ago`)
}

export function duration(from: string | null | undefined, to?: string | null, now = Date.now()) {
  const a = parseServerTime(from)
  if (!a) return '–'
  const b = to ? parseServerTime(to) : new Date(now)
  if (!b) return '–'
  const mins = Math.max(0, Math.round((b.getTime() - a.getTime()) / 60000))
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return digits(h ? `${h} h ${m} min` : `${m} min`)
}

/** YYYY-MM-DD of a date in Nepal time (for the API's date filters). */
export function nepalDay(d: Date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: NEPAL_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
  return parts // en-CA gives YYYY-MM-DD
}

export function addDays(day: string, n: number) {
  const d = new Date(`${day}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

export const titleCase = (s: string) => s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

/** 1.2K, 3.4L, 1.1Cr: axis labels. */
export function compact(n: number) {
  const a = Math.abs(n)
  if (a >= 1e7) return digits(`${num1.format(n / 1e7)}Cr`)
  if (a >= 1e5) return digits(`${num1.format(n / 1e5)}L`)
  if (a >= 1e3) return digits(`${num1.format(n / 1e3)}K`)
  return digits(int.format(n))
}
